import { useEffect, useState, useCallback } from "react";
import Icon from "./Icon";

/**
 * @param {Array} files A list of objects {path, fileType}. Path is the full file path (ex: '/my/path/item.txt'), each filePath is expected to start with a '/'. And fileType is 'Added', 'Removed', 'Modified'
 * @param {Function} onSelect The function called when a file is clicked.
 * @param {String} selectedFile The string value of the file to default the selection to.
 * @param {Boolean} openFolders Handles whether folders are opened out by default. If true all folders are opened.
 * @param {String} folderIcon The string value of a MaterialUi icon to render for a folder.
 * @param {String} fileIcon The string value of a MaterialUi icon to render for a file.
 */
const FileTreeView = ({
    files,
    onSelect,
    selectedFile = "",
    openFolders = true,
    folderIcon = "Folder",
    fileIcon = "Description"
}) => {

    const [currentSelectedFile, setCurrentSelectedFile] = useState(selectedFile);
    const [displayData, setDisplayData] = useState([]);

    useEffect(() => {
        setCurrentSelectedFile(selectedFile);
    }, [selectedFile, currentSelectedFile]);

    const getFileIcon = useCallback((fileType) => {
        switch (fileType) {
            case 'Added': {
                return 'Add';
            }
            case 'Removed': {
                return 'Remove';
            }
            case 'Modified':
            default: {
                return fileIcon;
            }
        }
    }, [fileIcon]);

    const getFileTypeCss = useCallback((fileType) => {
        switch (fileType) {
            case 'Added': {
                return 'text-success';
            }
            case 'Removed': {
                return 'text-danger';
            }
            case 'Modified':
            default: {
                break;
            }
        }
    }, []);

    const addRecursively = useCallback((obj, filePaths, fileType, index = 1) => {
        if (!filePaths[index]) {
            return;
        }

        const foundObject = obj.find(folder => folder.name === filePaths[index] && folder.isExpandable === true);
        if (foundObject) {
            addRecursively(foundObject.children, filePaths, fileType, index + 1);
        }
        else {
            if (filePaths.length - 1 === index) {
                obj.push({
                    isExpandable: false,
                    fullPath: filePaths.join('/'),
                    name: filePaths[index],
                    fileType,
                    fileCss: getFileTypeCss(fileType),
                    fileIcon: getFileIcon(fileType)
                });
            }
            else {
                const newFolder = {
                    isExpandable: true,
                    fullPath: filePaths.slice(0, index + 1).join('/'),
                    name: filePaths[index],
                    children: [],
                    isOpen: openFolders
                }
                obj.push(newFolder);
                addRecursively(newFolder.children, filePaths, fileType, index + 1);
            }
        }
    }, [openFolders, getFileIcon, getFileTypeCss]);

    const findAndOpenFolder = useCallback((arr, folder) => {
        const foundFolder = arr.find(item => item === folder);
        if (foundFolder) {
            foundFolder.isOpen = !foundFolder.isOpen;
            return arr;
        }

        arr.forEach(item => {
            if (item.isExpandable) {
                item.children = findAndOpenFolder(item.children, folder);
            }
        });
        return arr;
    }, []);

    const handleFolderClick = useCallback((folder) => {
        setDisplayData(prev => [...findAndOpenFolder(prev, folder)]);
    }, [findAndOpenFolder]);

    const handleFileClick = useCallback((file) => {
        setCurrentSelectedFile(file.fullPath);
        onSelect(file.fullPath);
    }, [onSelect]);

    useEffect(() => {
        if (files) {
            setDisplayData(() => {
                var newData = [];

                files.forEach(file => {
                    const filePaths = file.path.split('/');
                    addRecursively(newData, filePaths, file.type);
                });

                return newData;
            });
        }
    }, [files, addRecursively]);

    const renderFile = useCallback((key, index) => {
        return <li
            className={`tree-view-node ${currentSelectedFile === key.fullPath ? "active" : key.fileCss}`}
            onClick={() => handleFileClick(key)}
            key={index}>
            <div className="d-flex align-items-center">
                {key.fileIcon && <Icon icon={key.fileIcon} className="d-block" />}
                {key.name}
            </div>
        </li>
    }, [currentSelectedFile, handleFileClick]);

    const renderFolder = useCallback((key, index) => {
        return <div className="tree-view-expandable" key={index}>
            <li
                className={`tree-view-expandable-node ${currentSelectedFile.startsWith(key.fullPath) && !key.isOpen ? "active" : ''}`}
                onClick={() => handleFolderClick(key)}
            >
                <div className="d-flex align-items-center">
                    {folderIcon && <Icon icon={folderIcon} className="d-block" />}
                    <Icon icon={key.isOpen ? 'ArrowDropDown' : 'ArrowRight'} />
                    {key.fullPath}
                </div>
            </li>
            {key.children.length !== 0 && key.isOpen && key.children.map((child, index) => child.isExpandable ? renderFolder(child, index) : renderFile(child, index))}
        </div>
    }, [folderIcon, currentSelectedFile, renderFile, handleFolderClick]);

    return <ul className="tree-view">
        {displayData.length !== 0 &&
            displayData.map((key, index) => key.isExpandable ? renderFolder(key, index) : renderFile(key, index))
        }
    </ul>
};

export default FileTreeView;